import { forwardRef } from "react";
import Link from "next/link";

import { Anchor, Button, useModal } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import BaseCard from "@/components/BaseCard";

import AssignModal from "@/components/AssignModal";
import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";

import { useContentCard } from "../hooks/useContentCard";

import {
  CardViewmodelAndProps,
  InteractiveVideoCardMetadata,
  GenerationGeniusVideoMetadata,
} from "../ContentCardV2.interface";
import { CONTENT_TYPES_LABELS } from "../ContentCardV2.constants";

import { ContentTypeEnum } from "../ContentCardV2.interface";
import {
  addToAssignmentButtonCss,
  cardActionsContainerCss,
  contentSettingsModeCss,
  linkCss,
} from "./styles";

const AnchorWithTrackEvent = withTrackEvent(Anchor);
const ButtonWithTrackEvent = withTrackEvent(Button);

export const VideoCard = forwardRef<HTMLElement, CardViewmodelAndProps>(
  (props, ref) => {
    const {
      id: contentId,
      contentSettingsGroups,
      dataQaSelector = "",
      display_date: displayDate,
      images,
      isContentSettingsEnabled,
      metadata,
      title,
      url,
      visibility_markers: visibilityMarkers,
    } = props;

    const {
      content_type: contentType,
      duration_in_seconds: durationInSeconds,
      maturity_range: maturityRange = [],
    } = metadata as
      | InteractiveVideoCardMetadata
      | GenerationGeniusVideoMetadata;

    const isGenerationGenuis =
      contentType === ContentTypeEnum.GENERATION_GENIUS_VIDEO;

    const {
      assignHref,
      badges,
      createAssignmentUrl,
      durationMetadata,
      formattedMaturityGradeLevelLabel,
      showAssignmentAnchor,
    } = useContentCard({
      contentId,
      contentType,
      durationInSeconds,
      maturityRange,
      visibilityMarkers,
    });

    const [modalProps, setModalActive] = useModal(false);
    const closeModal = () => {
      setModalActive(false);
    };

    return (
      <BaseCard
        ref={ref}
        badges={badges}
        contentId={contentId}
        contentTag={CONTENT_TYPES_LABELS[contentType]}
        dataQaSelector={dataQaSelector}
        detailsText={
          formattedMaturityGradeLevelLabel
            ? `Maturity level: ${formattedMaturityGradeLevelLabel}`
            : null
        }
        displayDate={displayDate}
        featureTag={
          isGenerationGenuis
            ? {
                text: "Generation Genius",
                // Color not available in Angelou.
                backgroundColor: "rgba(241, 110, 0, 1)",
              }
            : null
        }
        images={images}
        metadataOverlayTag={durationMetadata}
        title={title}
        url={url}
        specialContent={
          <>
            {isContentSettingsEnabled && (
              <AdminContentSettingsCardFooter
                contentId={contentId}
                contentSettingsGroups={contentSettingsGroups || []}
                visibilityMarkers={visibilityMarkers}
                style={contentSettingsModeCss}
              />
            )}

            <div css={cardActionsContainerCss}>
              {!isGenerationGenuis ? (
                <ButtonWithTrackEvent
                  onClick={() => setModalActive(true)}
                  flavor={Button.flavors.muted}
                  size={Button.sizes.lg}
                  actionName={actionNames.interact}
                  objectType={objectTypes.content}
                  streamProperties={{
                    content_type: contentType,
                    description:
                      "Open add to assignment modal on interactive video content card",
                  }}
                  dataProps={{
                    "data-qa-selector":
                      "interactive-video-card-open-add-to-assignment-modal",
                  }}
                  __cssFor={addToAssignmentButtonCss}
                >
                  Add to assignment
                </ButtonWithTrackEvent>
              ) : null}

              {showAssignmentAnchor && (
                <AnchorWithTrackEvent
                  __cssFor={linkCss}
                  styleAnchorAsButton={{
                    flavor: Button.flavors.muted,
                    size: Button.sizes.lg,
                  }}
                  href={assignHref}
                  actionName={actionNames.navigate}
                  objectType={objectTypes.content}
                  streamProperties={{
                    content_type: contentType,
                    description:
                      "Navigate to create new assignment from video content card",
                  }}
                  __renderCustomLink={({ children, href, ...anchorProps }) => {
                    return (
                      <Link
                        href={typeof href === "string" ? href : ""}
                        data-qa-selector="video-card-assign-link"
                        {...anchorProps}
                      >
                        {children}
                      </Link>
                    );
                  }}
                >
                  Assign
                </AnchorWithTrackEvent>
              )}
            </div>

            {modalProps.isActive && (
              <AssignModal
                contentMemberIds={[contentId]}
                createAssignmentUrl={createAssignmentUrl}
                modalProps={modalProps}
                handleClickToCloseModal={closeModal}
              />
            )}
          </>
        }
      />
    );
  },
);
